// frontend/src/pages/Payments.tsx

import React, { useContext, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import { AuthContext } from "../context/AuthContext";
import { FiscalYearContext } from "../context/FiscalYearContext";

import { getPayments, createPayment } from "../api/payments";

import { Payment as PaymentType, CreatePaymentRequest } from "../types/payment";

import Sidebar from "../components/Sidebar";
import PaymentForm from "../components/PaymentForm";
import PaymentLogsModal from "../components/PaymentLogsModal";

import { Search, Download, Plus, Eye, Mail, X } from "lucide-react";

import { generatePaginationItems } from "../utils/pagination";

import { formatFiscalYear, isDateInFiscalYear } from "../utils/fiscalYear";

export default function Payments() {
  const { user } = useContext(AuthContext);
  const { fiscalYear } = useContext(FiscalYearContext);

  // ------------------------------------------------------------
  // Data
  // ------------------------------------------------------------

  const [list, setList] = useState<PaymentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  // ------------------------------------------------------------
  // Search + filters
  // ------------------------------------------------------------

  const [searchTerm, setSearchTerm] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [archiveFilter, setArchiveFilter] = useState<
    "all" | "active" | "archived"
  >("all");

  // ------------------------------------------------------------
  // Pagination
  // ------------------------------------------------------------

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // ------------------------------------------------------------
  // Details modal
  // ------------------------------------------------------------

  const [detailKey, setDetailKey] = useState<string | null>(null);

  // ------------------------------------------------------------
  // Load payments
  // ------------------------------------------------------------

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);

      const data = await getPayments(fiscalYear);

      if (!mounted) return;

      setList(data);
      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [fiscalYear]);

  // ------------------------------------------------------------
  // Group raw payments by student
  // ------------------------------------------------------------

  type Acc = {
    studentName: string;
    studentEmail: string;

    courseNames: Set<string>;

    receipts: string[];
    dates: string[];
    createdBys: string[];
    amounts: number[];
    discounts: number[];

    totalAmount: number;
    totalDiscount: number;

    ids: string[];

    courseStatuses: Record<string, "Full" | "Partial">;
  };

  const groupedList = useMemo(() => {
    const map: Record<string, Acc> = {};

    const raw = list
      .slice()
      .filter((p) => p.course)
      .sort(
        (a, b) =>
          new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime(),
      )
      .filter((p) => {
        const paymentDate = new Date(p.paymentDate);

        if (startDate && paymentDate < new Date(startDate)) {
          return false;
        }

        if (endDate && paymentDate > new Date(endDate)) {
          return false;
        }

        if (archiveFilter === "active" && p.course.isArchived) {
          return false;
        }

        if (archiveFilter === "archived" && !p.course.isArchived) {
          return false;
        }

        return true;
      });

    for (const p of raw) {
      const key = p.student?.id ?? `deleted:${p.studentEmail}`;

      if (!map[key]) {
        map[key] = {
          studentName: p.student?.name ?? p.studentName ?? "(deleted)",

          studentEmail: p.student?.email ?? p.studentEmail ?? "",

          courseNames: new Set(),

          receipts: [],
          dates: [],
          createdBys: [],
          amounts: [],
          discounts: [],

          totalAmount: 0,
          totalDiscount: 0,

          ids: [],

          courseStatuses: {},
        };
      }

      const acc = map[key];

      const courseName = p.course?.name || "Unknown Course";

      acc.courseNames.add(courseName);

      acc.receipts.push(p.receiptNumber || "Discount only");

      acc.dates.push(new Date(p.paymentDate).toLocaleDateString());

      acc.createdBys.push(p.createdBy?.username ?? p.createdByName);

      acc.amounts.push(p.amount);
      acc.discounts.push(p.discountAmount || 0);

      acc.totalAmount += p.amount;
      acc.totalDiscount += p.discountAmount || 0;

      acc.ids.push(p.id);

      acc.courseStatuses[courseName] = p.isFullyPaid ? "Full" : "Partial";
    }

    return Object.entries(map).map(([key, entry]) => {
      const overallStatus = Object.values(entry.courseStatuses).some(
        (status) => status === "Partial",
      )
        ? "Partial"
        : "Full";

      return {
        key,

        studentName: entry.studentName,

        studentEmail: entry.studentEmail,

        courseNames: Array.from(entry.courseNames).join(", "),

        receipts: entry.receipts.join(", "),

        dates: entry.dates.join(", "),

        createdBys: entry.createdBys.join(", "),

        individualAmounts: entry.amounts.join(", "),
        individualDiscounts: entry.discounts.join(", "),

        totalAmount: entry.totalAmount,
        totalDiscount: entry.totalDiscount,

        ids: entry.ids,

        status: overallStatus,

        courseStatuses: entry.courseStatuses,
      };
    });
  }, [list, startDate, endDate, archiveFilter]);

  // ------------------------------------------------------------
  // Courses available in filter
  // ------------------------------------------------------------

  const allCourses = useMemo(() => {
    const set = new Set<string>();

    groupedList.forEach((group) => {
      group.courseNames.split(", ").forEach((course) => set.add(course));
    });

    return Array.from(set).sort();
  }, [groupedList]);

  // ------------------------------------------------------------
  // Search + course + status filters
  // ------------------------------------------------------------

  const filteredList = useMemo(() => {
    return groupedList.filter((group) => {
      if (
        selectedCourse &&
        !group.courseNames.split(", ").includes(selectedCourse)
      ) {
        return false;
      }

      if (selectedStatus && group.status !== selectedStatus) {
        return false;
      }

      if (searchTerm) {
        const term = searchTerm.toLowerCase();

        if (
          !(
            group.studentName.toLowerCase().includes(term) ||
            group.studentEmail.toLowerCase().includes(term) ||
            group.courseNames.toLowerCase().includes(term)
          )
        ) {
          return false;
        }
      }

      return true;
    });
  }, [groupedList, selectedCourse, selectedStatus, searchTerm]);

  // ------------------------------------------------------------
  // Pagination
  // ------------------------------------------------------------

  const totalPages = Math.ceil(filteredList.length / rowsPerPage);

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;

    return filteredList.slice(start, start + rowsPerPage);
  }, [filteredList, currentPage, rowsPerPage]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * rowsPerPage;

  // ------------------------------------------------------------
  // Payment details
  // ------------------------------------------------------------

  const detailPayments = useMemo(() => {
    if (!detailKey) return [];

    return list.filter(
      (payment) =>
        (payment.student?.id ?? `deleted:${payment.studentEmail}`) ===
        detailKey,
    );
  }, [list, detailKey]);

  const detailGroup = useMemo(() => {
    if (!detailKey) return null;

    return groupedList.find((group) => group.key === detailKey) ?? null;
  }, [groupedList, detailKey]);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // ------------------------------------------------------------
  // Create payment
  // ------------------------------------------------------------

  const handleNew = async (data: CreatePaymentRequest) => {
    const payment = await createPayment(data);

    if (isDateInFiscalYear(payment.paymentDate, fiscalYear)) {
      setList((previous) => [payment, ...previous]);
    }

    setShowNew(false);
  };

  // ------------------------------------------------------------
  // Export
  // ------------------------------------------------------------

  const handleExport = () => {
    const toExport = filteredList;

    const headers = [
      "Student",
      "Email",
      "Courses",
      "Dates",
      "Receipts",
      "Individual Amounts",
      "Total Amount",
      "Individual Discounts",
      "Total Discount",
      "Created By",
      "Status",
    ];

    const rows = toExport.map((group) => [
      group.studentName,
      group.studentEmail,
      `"${group.courseNames}"`,
      `"${group.dates}"`,
      `"${group.receipts}"`,
      `"${group.individualAmounts}"`,
      group.totalAmount.toString(),
      `"${group.individualDiscounts}"`,
      group.totalDiscount.toString(),
      `"${group.createdBys}"`,
      group.status,
    ]);

    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join(
      "\r\n",
    );

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    anchor.href = url;

    anchor.download = `payments_${new Date().toISOString()}.csv`;

    anchor.click();

    URL.revokeObjectURL(url);
  };

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------

  const formatCurrency = (amount: number) =>
    `Rs ${amount.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedCourse("");
    setSelectedStatus("");
    setArchiveFilter("all");
    setSearchTerm("");
  };

  const fieldClass = `
        box-border h-10 min-w-0 w-full max-w-full rounded-[9px]
        border border-slate-200 bg-white px-3
        text-[12px] font-medium text-slate-800
        outline-none transition
        placeholder:text-slate-400
        hover:border-slate-300
        focus:border-slate-400
        focus:ring-2 focus:ring-slate-100
    `;

  const iconButtonClass = `
        inline-flex h-8 w-8 shrink-0 items-center justify-center
        rounded-lg border border-slate-200 bg-white
        p-0 text-slate-500 transition
        hover:border-slate-300
        hover:bg-slate-50
        hover:text-slate-900
        focus:outline-none
        focus:ring-2
        focus:ring-slate-200
    `;

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Sidebar />

      <main
        className="
                    ml-[280px] min-w-0 px-6 py-6
                    transition-[margin] duration-200
                    [.sidebar-collapsed_&]:ml-[76px]
                    max-lg:ml-[76px]
                    max-lg:px-4
                    max-sm:px-3
                "
      >
        <div className="mx-auto w-full max-w-[1600px]">
          {/* ====================================================
                        Header
                    ==================================================== */}

          <header
            className="
                            mb-5 flex items-start justify-between gap-4
                            max-md:flex-col
                            max-md:items-stretch
                        "
          >
            <div className="flex items-start gap-3">
              <div
                className="
                                    grid h-10 w-10 shrink-0
                                    place-items-center
                                    rounded-[10px]
                                    bg-slate-100
                                    text-slate-600
                                "
              >
                <Download size={20} strokeWidth={1.8} />
              </div>

              <div>
                <div
                  className="
                                        mb-1
                                        text-[10px]
                                        font-bold
                                        uppercase
                                        tracking-[0.12em]
                                        text-blue-600
                                    "
                >
                  Payment management
                </div>

                <h1
                  className="
                                        m-0
                                        text-[28px]
                                        font-bold
                                        tracking-[-0.03em]
                                        text-slate-950
                                        max-sm:text-[24px]
                                    "
                >
                  Payments
                </h1>

                <p
                  className="
                                        mb-0 mt-1
                                        text-[12px]
                                        leading-5
                                        text-slate-500
                                    "
                >
                  Manage and track payments for {formatFiscalYear(fiscalYear)}
                </p>
              </div>
            </div>

            <div
              className="
                                flex shrink-0
                                items-center
                                gap-2
                                max-sm:grid
                                max-sm:grid-cols-2
                            "
            >
              {user.role !== "viewer" && (
                <button
                  type="button"
                  onClick={handleExport}
                  className="
                                        inline-flex h-10
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-[9px]
                                        border
                                        border-slate-200
                                        bg-white
                                        px-4
                                        text-[11px]
                                        font-semibold
                                        text-slate-600
                                        transition
                                        hover:border-slate-300
                                        hover:bg-slate-50
                                        hover:text-slate-900
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-slate-200
                                    "
                >
                  <Download size={15} />
                  Export CSV
                </button>
              )}

              <button
                type="button"
                onClick={() => setShowNew(true)}
                className="
    inline-flex h-10
    items-center
    justify-center
    gap-2
    rounded-[9px]
    border
    border-emerald-600
    bg-emerald-600
    px-4
    text-[11px]
    font-semibold
    text-white
    transition
    hover:border-emerald-700
    hover:bg-emerald-700
    focus:outline-none
    focus:ring-2
    focus:ring-emerald-200
  "
              >
                <Plus size={15} />
                Record Payment
              </button>
            </div>
          </header>

          {/* ====================================================
                        Search + filters
                    ==================================================== */}

          <section
            className="
                            mb-4
                            rounded-[16px]
                            border
                            border-slate-200
                            bg-white
                            p-4
                            shadow-[0_1px_3px_rgba(15,23,42,0.04)]
                        "
          >
            <div
              className="
                                flex flex-col gap-3
                                md:flex-row
                                md:items-end
                                md:gap-4
                            "
            >
              <label className="min-w-0 flex-1">
                <span
                  className="
                                        mb-1.5 block
                                        text-[10px]
                                        font-semibold
                                        uppercase
                                        tracking-[0.04em]
                                        text-slate-400
                                    "
                >
                  Search payments
                </span>

                <div className="relative">
                  <Search
                    size={16}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <input
                    type="text"
                    aria-label="Search payments"
                    placeholder="Search students, emails, or courses"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    className={`${fieldClass} pl-9`}
                  />
                </div>
              </label>

              <div
                className="
                                    flex shrink-0
                                    items-end
                                    md:w-[124px]
                                "
              >
                <button
                  type="button"
                  onClick={() => setShowFilter((current) => !current)}
                  className={`
                                        inline-flex h-10 w-full
                                        items-center
                                        justify-center
                                        gap-2
                                        rounded-[9px]
                                        border
                                        px-4
                                        text-[11px]
                                        font-semibold
                                        transition
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-slate-200
                                        ${
                                          showFilter
                                            ? `
                                                    border-slate-900
                                                    bg-slate-950
                                                    text-white
                                                    hover:bg-slate-800
                                                `
                                            : `
                                                    border-slate-200
                                                    bg-white
                                                    text-slate-600
                                                    hover:border-slate-300
                                                    hover:bg-slate-50
                                                    hover:text-slate-900
                                                `
                                        }
                                    `}
                >
                  <Search size={14} />

                  {showFilter ? "Hide filters" : "Filters"}
                </button>
              </div>
            </div>

            {showFilter && (
              <div
                className="
                                    mt-4
                                    border-t
                                    border-slate-100
                                    pt-4
                                "
              >
                <div
                  className="
                                        grid grid-cols-1
                                        gap-3
                                        sm:grid-cols-2
                                        lg:grid-cols-5
                                    "
                >
                  {/* From date */}

                  <label className="min-w-0">
                    <span
                      className="
                                                mb-1.5 block
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.04em]
                                                text-slate-400
                                            "
                    >
                      From date
                    </span>

                    <input
                      type="date"
                      value={startDate}
                      onChange={(event) => setStartDate(event.target.value)}
                      className={fieldClass}
                    />
                  </label>

                  {/* To date */}

                  <label className="min-w-0">
                    <span
                      className="
                                                mb-1.5 block
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.04em]
                                                text-slate-400
                                            "
                    >
                      To date
                    </span>

                    <input
                      type="date"
                      value={endDate}
                      onChange={(event) => setEndDate(event.target.value)}
                      className={fieldClass}
                    />
                  </label>

                  {/* Course */}

                  <label className="min-w-0">
                    <span
                      className="
                                                mb-1.5 block
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.04em]
                                                text-slate-400
                                            "
                    >
                      Courses
                    </span>

                    <select
                      value={selectedCourse}
                      onChange={(event) =>
                        setSelectedCourse(event.target.value)
                      }
                      className={fieldClass}
                    >
                      <option value="">Select course...</option>

                      {allCourses.map((course) => (
                        <option key={course} value={course}>
                          {course}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Status */}

                  <label className="min-w-0">
                    <span
                      className="
                                                mb-1.5 block
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.04em]
                                                text-slate-400
                                            "
                    >
                      Status
                    </span>

                    <select
                      value={selectedStatus}
                      onChange={(event) =>
                        setSelectedStatus(event.target.value)
                      }
                      className={fieldClass}
                    >
                      <option value="">Select status...</option>

                      <option value="Full">Full</option>

                      <option value="Partial">Partial</option>
                    </select>
                  </label>

                  {/* Archive */}

                  <label className="min-w-0">
                    <span
                      className="
                                                mb-1.5 block
                                                text-[10px]
                                                font-semibold
                                                uppercase
                                                tracking-[0.04em]
                                                text-slate-400
                                            "
                    >
                      Course Archive
                    </span>

                    <select
                      value={archiveFilter}
                      onChange={(event) =>
                        setArchiveFilter(
                          event.target.value as "all" | "active" | "archived",
                        )
                      }
                      className={fieldClass}
                    >
                      <option value="all">All Courses</option>

                      <option value="active">Active Only</option>

                      <option value="archived">Archived Only</option>
                    </select>
                  </label>
                </div>

                <div
                  className="
                                        mt-4
                                        flex flex-wrap
                                        items-center
                                        justify-between
                                        gap-2
                                        border-t
                                        border-slate-100
                                        pt-3
                                    "
                >
                  <span
                    className="
                                            text-[10px]
                                            text-slate-400
                                        "
                  >
                    Filters apply to payment records.
                  </span>

                  <button
                    type="button"
                    onClick={clearFilters}
                    className="
                                            inline-flex h-8
                                            items-center
                                            justify-center
                                            gap-1.5
                                            rounded-lg
                                            border
                                            border-slate-200
                                            bg-white
                                            px-3
                                            text-[13px]
                                            font-semibold
                                            text-slate-600
                                            transition
                                            hover:border-slate-300
                                            hover:bg-slate-50
                                            hover:text-slate-900
                                        "
                  >
                    <X size={13} />
                    Clear filters
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* ====================================================
                        Loading
                    ==================================================== */}

          {loading ? (
            <section
              className="
                                flex min-h-[260px]
                                flex-col
                                items-center
                                justify-center
                                rounded-[16px]
                                border
                                border-slate-200
                                bg-white
                                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
                            "
            >
              <span
                className="
                                    h-6 w-6
                                    animate-spin
                                    rounded-full
                                    border-[3px]
                                    border-slate-200
                                    border-t-slate-700
                                "
              />

              <span
                className="
                                    mt-3
                                    text-[11px]
                                    font-medium
                                    text-slate-500
                                "
              >
                Loading payments…
              </span>
            </section>
          ) : (
            /* ====================================================
                            Table card
                        ==================================================== */

            <section
              className="
                                overflow-hidden
                                rounded-[16px]
                                border
                                border-slate-200
                                bg-white
                                shadow-[0_1px_3px_rgba(15,23,42,0.04)]
                            "
            >
              {/* Table header */}

              <div
                className="
                                    flex items-center
                                    justify-between
                                    gap-4
                                    border-b
                                    border-slate-100
                                    px-5
                                    py-4
                                    max-sm:flex-col
                                    max-sm:items-start
                                    max-sm:px-4
                                "
              >
                <div>
                  <h2
                    className="
                                            m-0
                                            text-[18px]
                                            font-bold
                                            text-slate-950
                                        "
                  >
                    Payments
                  </h2>

                  <p
                    className="
                                            mb-0 mt-1
                                            text-[13px]
                                            text-slate-500
                                        "
                  >
                    Showing{" "}
                    <strong
                      className="
                                                font-semibold
                                                text-slate-600
                                            "
                    >
                      {filteredList.length}
                    </strong>{" "}
                    payment
                    {filteredList.length === 1 ? "" : "s"}
                  </p>
                </div>

                <div
                  className="
                                        flex flex-wrap
                                        items-center
                                        gap-2
                                    "
                >
                  <div
                    className="
                                            rounded-lg
                                            bg-slate-50
                                            px-3
                                            py-2
                                            text-[13px]
                                            text-slate-500
                                        "
                  >
                    <strong
                      className="
                                                font-semibold
                                                text-slate-800
                                            "
                    >
                      {paginatedList.length}
                    </strong>{" "}
                    on this page
                  </div>
                </div>
              </div>

              {paginatedList.length > 0 ? (
                <>
                  {/* Table */}

                  <div className="w-full overflow-x-auto">
                    <table className="w-full min-w-[1250px] border-collapse">
                      <thead className="bg-slate-50/80">
                        <tr>
                          {[
                            "Student",
                            "Courses",
                            "Dates",
                            "Receipts",
                            "Payment / Discount",
                            "Status",
                            "Created By",
                          ].map((label) => (
                            <th
                              key={label}
                              className="px-4 py-3.5 text-left text-[14px] font-semibold uppercase
                                                                    tracking-[0.04em]
                                                                    text-slate-600
                                                                "
                            >
                              {label}
                            </th>
                          ))}

                          <th
                            className="
                                                            w-[100px]
                                                            px-4
                                                            py-3.5
                                                            text-center
                                                            text-[14px]
                                                            font-semibold
                                                            uppercase
                                                            tracking-[0.04em]
                                                            text-slate-600
                                                        "
                          >
                            Actions
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginatedList.map((group) => {
                          return (
                            <tr
                              key={group.key}
                              className="
                                                                    border-b
                                                                    border-slate-100
                                                                    transition-colors
                                                                    last:border-b-0
                                                                    hover:bg-slate-50/60
                                                                "
                            >
                              {/* Student */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <div className="min-w-[220px]">
                                  <strong
                                    className="
                                                                                block
                                                                                max-w-[220px]
                                                                                truncate
                                                                                text-[15px]
                                                                                font-semibold
                                                                                text-slate-950
                                                                            "
                                    title={group.studentName}
                                  >
                                    {group.studentName}
                                  </strong>

                                  <div
                                    className="
                                                                                mt-1
                                                                                flex
                                                                                max-w-[240px]
                                                                                items-center
                                                                                gap-1.5
                                                                                text-[13px]
                                                                                text-slate-500
                                                                            "
                                  >
                                    <Mail
                                      size={13}
                                      className="
                                                                                    shrink-0
                                                                                    text-slate-400
                                                                                "
                                    />

                                    <span
                                      className="truncate"
                                      title={group.studentEmail}
                                    >
                                      {group.studentEmail || "—"}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              {/* Courses */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <div
                                  className="
                                                                            flex
                                                                            min-w-[150px]
                                                                            max-w-[240px]
                                                                            flex-wrap
                                                                            gap-1.5
                                                                        "
                                >
                                  {group.courseNames
                                    .split(", ")
                                    .map((course, courseIndex) => (
                                      <span
                                        key={courseIndex}
                                        className="
                                                                                            inline-flex
                                                                                            max-w-[220px]
                                                                                            rounded-full
                                                                                            bg-slate-100
                                                                                            px-2.5
                                                                                            py-1
                                                                                            text-[12px]
                                                                                            font-semibold
                                                                                            text-slate-700
                                                                                        "
                                        title={course}
                                      >
                                        <span className="truncate">
                                          {course}
                                        </span>
                                      </span>
                                    ))}
                                </div>
                              </td>

                              {/* Dates */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <div
                                  className="
                                                                            min-w-[115px]
                                                                            space-y-1
                                                                            text-[13px]
                                                                            font-medium
                                                                            text-slate-700
                                                                        "
                                >
                                  {group.dates
                                    .split(", ")
                                    .map((date, dateIndex) => (
                                      <div
                                        key={dateIndex}
                                        className="whitespace-nowrap"
                                      >
                                        {date}
                                      </div>
                                    ))}
                                </div>
                              </td>

                              {/* Receipts */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <div
                                  className="
                                                                            min-w-[130px]
                                                                            space-y-1
                                                                        "
                                >
                                  {group.receipts
                                    .split(", ")
                                    .map((receipt, receiptIndex) => (
                                      <code
                                        key={receiptIndex}
                                        className="
                                                                                            block
                                                                                            max-w-[180px]
                                                                                            truncate
                                                                                            text-[12px]
                                                                                            text-slate-500
                                                                                        "
                                        title={receipt}
                                      >
                                        {receipt}
                                      </code>
                                    ))}
                                </div>
                              </td>

                              {/* Amount */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <div className="min-w-[150px]">
                                  <div
                                    className="
                                                                                whitespace-nowrap
                                                                                text-[14px]
                                                                                font-semibold
                                                                                text-slate-900
                                                                            "
                                  >
                                    {formatCurrency(group.totalAmount)}
                                  </div>

                                  {group.totalDiscount > 0 && (
                                    <div className="mt-1 text-[12px] font-medium text-emerald-700">
                                      Discount: {formatCurrency(group.totalDiscount)}
                                    </div>
                                  )}

                                  {group.individualAmounts.split(", ").length >
                                    1 && (
                                    <div
                                      className="
                                                                                    mt-1
                                                                                    max-w-[220px]
                                                                                    text-[12px]
                                                                                    leading-5
                                                                                    text-slate-500
                                                                                "
                                    >
                                      {group.individualAmounts
                                        .split(", ")
                                        .map((amount) =>
                                          formatCurrency(parseFloat(amount)),
                                        )
                                        .join(" + ")}
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* Status */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <span
                                  className={`
                                                                            inline-flex
                                                                            rounded-full
                                                                            px-2.5
                                                                            py-1
                                                                            text-[13px]
                                                                            font-semibold
                                                                            ${
                                                                              group.status ===
                                                                              "Partial"
                                                                                ? `
                                                                                        bg-amber-50
                                                                                        text-amber-700
                                                                                    `
                                                                                : `
                                                                                        bg-emerald-50
                                                                                        text-emerald-700
                                                                                    `
                                                                            }
                                                                        `}
                                >
                                  {group.status}
                                </span>
                              </td>

                              {/* Created by */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        align-middle
                                                                    "
                              >
                                <div
                                  className="
                                                                            min-w-[130px]
                                                                            max-w-[200px]
                                                                            break-words
                                                                            text-[13px]
                                                                            text-slate-600
                                                                        "
                                  title={group.createdBys}
                                >
                                  {group.createdBys || "—"}
                                </div>
                              </td>

                              {/* Actions */}

                              <td
                                className="
                                                                        px-4
                                                                        py-4
                                                                        text-center
                                                                        align-middle
                                                                    "
                              >
                                <button
                                  type="button"
                                  onClick={() => setDetailKey(group.key)}
                                  className={iconButtonClass}
                                  title="View Logs"
                                  aria-label={`View payment logs for ${group.studentName}`}
                                >
                                  <Eye size={15} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* ====================================================
                                        Pagination
                                    ==================================================== */}

                  <div
                    className="
                                            flex
                                            items-center
                                            justify-between
                                            gap-4
                                            border-t
                                            border-slate-100
                                            px-5
                                            py-3
                                            max-sm:flex-col
                                            max-sm:items-stretch
                                            max-sm:px-4
                                        "
                  >
                    <div
                      className="
                                                flex flex-wrap
                                                items-center
                                                gap-2
                                                text-[13px]
                                                text-slate-600
                                            "
                    >
                      <span>Rows per page</span>

                      <select
                        value={rowsPerPage}
                        onChange={(event) => {
                          setRowsPerPage(Number(event.target.value));

                          setCurrentPage(1);
                        }}
                        className="
                                                    h-8
                                                    rounded-lg
                                                    border
                                                    border-slate-200
                                                    bg-white
                                                    px-2
                                                    text-[13px]
                                                    font-medium
                                                    text-slate-700
                                                    outline-none
                                                    focus:border-slate-400
                                                    focus:ring-2
                                                    focus:ring-slate-100
                                                "
                      >
                        {[5, 10, 20, 50].map((count) => (
                          <option key={count} value={count}>
                            {count}
                          </option>
                        ))}
                      </select>

                      <span>
                        {startIndex + 1}–
                        {Math.min(
                          startIndex + rowsPerPage,
                          filteredList.length,
                        )}{" "}
                        of {filteredList.length}
                      </span>
                    </div>

                    {filteredList.length > rowsPerPage && (
                      <div
                        className="
                                                    flex
                                                    flex-wrap
                                                    items-center
                                                    justify-end
                                                    gap-1.5
                                                    max-sm:justify-start
                                                "
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((page) => Math.max(page - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className="
                                                        h-8
                                                        rounded-lg
                                                        border
                                                        border-slate-200
                                                        bg-white
                                                        px-3
                                                        text-[13px]
                                                        font-semibold
                                                        text-slate-600
                                                        transition
                                                        hover:bg-slate-50
                                                        disabled:cursor-not-allowed
                                                        disabled:opacity-40
                                                    "
                        >
                          Prev
                        </button>

                        {generatePaginationItems(currentPage, totalPages).map(
                          (item, index) =>
                            item.type === "ellipsis" ? (
                              <span
                                key={`ellipsis-${index}`}
                                className="
                                                                    inline-flex
                                                                    h-8
                                                                    min-w-8
                                                                    items-center
                                                                    justify-center
                                                                    text-[13px]
                                                                    font-semibold
                                                                    text-slate-500
                                                                "
                              >
                                …
                              </span>
                            ) : (
                              <button
                                type="button"
                                key={item.page}
                                onClick={() => setCurrentPage(item.page!)}
                                className={`
                                                                    h-8
                                                                    min-w-8
                                                                    rounded-lg
                                                                    border
                                                                    px-2
                                                                    text-[13px]
                                                                    font-semibold
                                                                    transition
                                                                    ${
                                                                      item.isActive
                                                                        ? `
                                                                                border-slate-950
                                                                                bg-slate-950
                                                                                text-white
                                                                            `
                                                                        : `
                                                                                border-slate-200
                                                                                bg-white
                                                                                text-slate-600
                                                                                hover:bg-slate-50
                                                                            `
                                                                    }
                                                                `}
                              >
                                {item.page}
                              </button>
                            ),
                        )}

                        <button
                          type="button"
                          onClick={() =>
                            setCurrentPage((page) =>
                              Math.min(page + 1, totalPages),
                            )
                          }
                          disabled={currentPage === totalPages}
                          className="
                                                        h-8
                                                        rounded-lg
                                                        border
                                                        border-slate-200
                                                        bg-white
                                                        px-3
                                                        text-[13px]
                                                        font-semibold
                                                        text-slate-600
                                                        transition
                                                        hover:bg-slate-50
                                                        disabled:cursor-not-allowed
                                                        disabled:opacity-40
                                                    "
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* Empty state */

                <div
                  className="
                                        flex
                                        min-h-[230px]
                                        flex-col
                                        items-center
                                        justify-center
                                        px-6
                                        text-center
                                    "
                >
                  <div
                    className="
                                            grid
                                            h-11 w-11
                                            place-items-center
                                            rounded-xl
                                            bg-slate-100
                                            text-slate-400
                                        "
                  >
                    <Search size={20} />
                  </div>

                  <h3
                    className="
                                            mb-0 mt-3
                                            text-[13px]
                                            font-semibold
                                            text-slate-900
                                        "
                  >
                    No payments found
                  </h3>

                  <p
                    className="
                                            mb-0 mt-1
                                            max-w-[430px]
                                            text-[10px]
                                            leading-5
                                            text-slate-500
                                        "
                  >
                    Try changing your search or filters to see more payment
                    records.
                  </p>
                </div>
              )}
            </section>
          )}

          {/* ====================================================
                        Record Payment Modal
                    ==================================================== */}

          {showNew && (
            <PaymentForm
              onSubmit={handleNew}
              onCancel={() => setShowNew(false)}
            />
          )}

          {/* ====================================================
                        Payment Logs Modal
                    ==================================================== */}

          {detailKey && detailGroup && (
            <PaymentLogsModal
              isOpen
              onClose={() => setDetailKey(null)}
              payments={detailPayments}
              studentName={detailGroup.studentName}
            />
          )}
        </div>
      </main>
    </div>
  );
}
